import React, { useState } from "react";
import cn from "classnames";
import moment from "moment";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import style from "./AviationBidChangeCard.module.scss";
import { Aviation } from "../../../../../services";
import { numberToFinanceFormat } from "../../../../../utils";
import ChevronIcon from "../../../../shared/icons/ChevronIcon";
import InfoIcon from "../../../../shared/icons/InfoIcon";
import ArrowBidIcon from "../../../../shared/icons/ArrowBidIcon";

const STATUS_BADGE = {
  awaiting: { key: "awaiting", className: "badge_awaiting" },
  accepted: { key: "accepted", className: "badge_accepted" },
  confirmed: { key: "accepted", className: "badge_accepted" },
  rejected: { key: "rejected", className: "badge_rejected" },
};

const parseChangedPrice = (value) => {
  const rawValue = (value || "").trim();
  if (!rawValue) return { oldPrice: "", newPrice: "", currency: "" };

  const currencyMatch = rawValue.match(/[^\d\s.,]+$/);
  const currency = currencyMatch ? currencyMatch[0].trim() : "";
  const priceValue = currencyMatch ? rawValue.slice(0, currencyMatch.index) : rawValue;
  const prices = priceValue
    .split(/->|→|—|–|-|>/)
    .map((price) => price.trim())
    .filter(Boolean);
  const formatPrice = (price) => {
    const numberValue = Number(price.replace(/[^\d.]/g, ""));
    return Number.isFinite(numberValue) ? numberToFinanceFormat(numberValue, true) : price;
  };

  return {
    oldPrice: prices[0] ? formatPrice(prices[0]) : "",
    newPrice: prices[1] ? formatPrice(prices[1]) : prices[0] ? formatPrice(prices[0]) : "",
    currency,
  };
};

const AviationBidChangeCard = ({ item, canDecide = true, onDone }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const priceStatus = (item?.changedPriceStatus || "").toLowerCase();
  const badge = STATUS_BADGE[priceStatus];
  const isAwaiting = priceStatus === "awaiting";
  const isAccepted = priceStatus === "accepted" || priceStatus === "confirmed";
  const { oldPrice, newPrice, currency } = parseChangedPrice(item?.changedPriceText);
  const date = item?.createdAt ? moment(item.createdAt).format("DD.MM.YYYY, HH:mm") : "";

  const decisionText =
    priceStatus === "rejected"
      ? t("orderInfoAviation.bidChange.decisionRejected", { price: newPrice, currency })
      : isAccepted
      ? t("orderInfoAviation.bidChange.decisionAccepted", { price: newPrice, currency })
      : "";

  const handleDecision = async (type) => {
    if (!item?.tenderBidId || isLoading) return;

    try {
      setIsLoading(true);
      await Aviation.decideAirTenderNewPrice(type, item.tenderBidId);
      toast.success(
        t(
          type === "accepted"
            ? "orderInfoAviation.bidChange.acceptSuccess"
            : "orderInfoAviation.bidChange.rejectSuccess",
        ),
      );
      await onDone?.();
    } catch (error) {
      console.error(error);
      toast.error(t("orderInfoAviation.bidChange.decisionError"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={cn(style.card, { [style.card_awaiting]: isAwaiting })}>
      <button
        type="button"
        className={cn(style.header, { [style.header_open]: isOpen })}
        onClick={() => setIsOpen((currentValue) => !currentValue)}
      >
        <span className={style.header_left}>
          <span className={style.title}>{t("orderInfoAviation.bidChange.title")}</span>
          {badge ? (
            <span className={cn(style.badge, style[badge.className])}>
              <span className={style.badge_dot} />
              {t(`orderInfoAviation.bidChange.status.${badge.key}`)}
            </span>
          ) : null}
        </span>
        <span className={style.header_right}>
          {date ? <span className={style.date}>{date}</span> : null}
          <span className={cn(style.chevron, { [style.chevron_open]: isOpen })}>
            <ChevronIcon />
          </span>
        </span>
      </button>

      {isOpen ? (
        <div className={style.body}>
          <div className={style.compare}>
            <div className={style.compare_col}>
              <span className={style.compare_label}>{t("orderInfoAviation.bidChange.before")}</span>
              <span className={cn(style.compare_value, style.compare_old)}>
                {oldPrice} {currency}
              </span>
            </div>
            <span className={style.compare_arrow}>
              <ArrowBidIcon />
            </span>
            <div className={style.compare_col}>
              <span className={style.compare_label}>{t("orderInfoAviation.bidChange.after")}</span>
              <span className={cn(style.compare_value, style.compare_new)}>
                {newPrice} {currency}
              </span>
            </div>
          </div>

          {item?.comment ? (
            <div className={style.reason}>
              <InfoIcon type={2} />
              <span className={style.reason_text}>
                {t("orderInfoAviation.bidChange.reason", { comment: item.comment })}
              </span>
            </div>
          ) : null}

          {decisionText ? (
            <p
              className={cn(style.decision_note, {
                [style.decision_note_rejected]: priceStatus === "rejected",
                [style.decision_note_accepted]: isAccepted,
              })}
            >
              {decisionText}
            </p>
          ) : null}

          {canDecide && isAwaiting ? (
            <div className={style.actions}>
              <button
                type="button"
                className={style.btn_accept}
                onClick={() => handleDecision("accepted")}
                disabled={isLoading}
              >
                {t("orderInfoAviation.bidChange.accept")}
              </button>
              <button
                type="button"
                className={style.btn_reject}
                onClick={() => handleDecision("rejected")}
                disabled={isLoading}
              >
                {t("orderInfoAviation.bidChange.reject")}
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default AviationBidChangeCard;
